import React, { useState, useEffect, useContext } from "react";
import * as Sentry from "@sentry/react";
import { 
  IonSpinner, 
  IonCard, 
  IonCardContent, 
  IonButton, 
  IonIcon, 
  IonLabel,
  IonItem,
  IonList,
  IonAvatar,
  IonBadge,
  useIonAlert,
  IonSegment,
  IonSegmentButton,
  IonGrid,
  IonRow,
  IonCol,
  IonModal,
  IonHeader,
  IonToolbar,
  IonButtons,
  useIonToast,
} from "@ionic/react";
import { ProfileContext } from "../../context/ProfileContext";

import { db } from "../../firebase";
import { 
  doc, 
  query, 
  collectionGroup, 
  where, 
  onSnapshot,
  getDocs,
  updateDoc,
  setDoc,
  runTransaction
} from "firebase/firestore";
import { 
  chatboxOutline, 
  cashOutline, 
  checkmark, 
  navigate, 
  peopleOutline,
  calendarOutline,
  locationOutline,
  arrowUndoOutline,
  megaphoneOutline,
  closeOutline,
  arrowUndo
} from "ionicons/icons";
import { format } from "date-fns";
import { useHistory } from "react-router-dom";
import { generateMapUrls, parseNotes2 } from "../../functions/FX_ParseAndMap";
import { 
  WORKFLOW_WERKER_TYPES, 
  WORKFLOW_SCHEDULER_TYPES,
  PUSH_NOTIFICATION_TYPES 
} from "../../functions/postActionTypes";
import { fx_FB_SendNotification } from "../../functions/Fx_SendPushNotification";
import { fx_FB_WerkerUpdateAll } from "../../functions/Fx_WerkerUpdateAll";
import { fx_FB_WerkerFeedUpdateAll } from "../../functions/fx_FB_WerkerFeedUpdateAll";
import { SchedDetailsComponent } from "../ShiftDetailsSchedComponents/Details";
import { SchedWerkersComponent } from "../ShiftDetailsSchedComponents/Werkers";
import { SchedJobData } from "../../hooks/scheduler/useJobData";
import { ChatPanel } from "./ChatPanel";
import { useJobActions } from "../../hooks/scheduler/useJobActions";
import "../../theme/AppStyles.css";

interface JobDetailPanelProps {
  jobId: string | null;
  isScheduler: boolean;
  onEditRequest?: (jobDocId: string) => void;
  onCloneRequest?: (jobDocId: string) => void;
  onEditWerkersRequest?: (result: { nextView: "selectWerkers" | "selectWerkersSeries"; jobId?: string; seriesId?: string }) => void;
  onJobDeleted?: () => void;
}

interface JobData {
  JobDocId: string;
  WerkerUid: string;
  WerkerFirstName: string;
  WerkerLastName: string;
  WerkerProfilePicURL: string;
  SchedulerUid: string;
  SchedFirstName: string;
  SchedLastName: string;
  SchedProfilePic: string;
  TotalJobSlots: number;
  UniqueJobDocId: string;
  JobIdentifier: string;
  Pay: string;
  Company: string;
  StartDate: string;
  EndDate: string;
  Location: string;
  LocationCoordinates: string;
  Notes: string;
  IsPaidJob: boolean;
  WerkersPaid: boolean;
  SchedJobStatus: string;
  WerkerJobStatus: string;
  PaidWerker: boolean;
  IsNewMessage: boolean;
  IsCancelled: boolean;
  ScheduledCount: number;
  MyJobDocId?: string;
  SchedUid?: string;
}

export const JobDetailPanel: React.FC<JobDetailPanelProps> = ({ jobId, isScheduler, onEditRequest, onCloneRequest, onEditWerkersRequest, onJobDeleted }) => {
  const { profile, myChats } = useContext(ProfileContext);
  const history = useHistory();
  const [present] = useIonAlert();
  const [presentToast] = useIonToast();
  const [loading, setLoading] = useState(false);
  const [jobData, setJobData] = useState<JobData | null>(null);
  const [scheduledWerkers, setScheduledWerkers] = useState<JobData[]>([]);
  const [segmentValue, setSegmentValue] = useState<string>("details");
  const [schedJobData, setSchedJobData] = useState<SchedJobData | null>(null);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [modalChatId, setModalChatId] = useState<string | null>(null);

  // Hook for scheduler job actions
  const {
    handleReminder,
    cancelJob,
    markComplete,
    unCompleteJob,
    unCloseOut,
    deleteJob,
  } = useJobActions({
    jobId: jobId || "",
    userId: profile?.UserId || "",
    userFirstName: profile?.FirstName || "",
    jobPay: jobData?.Pay || "",
    jobCompany: jobData?.Company || "",
    jobStartTime: jobData?.StartDate || "",
    jobStartTimeRaw: schedJobData?.StartDateTimeRaw || "",
    presentToast: presentToast,
    onConfirmReminder: (callback) => {
      present({
        header: "Re-alert everyone on this job?",
        buttons: [
          "Cancel",
          {
            text: "Ok",
            handler: () => {
              callback();
              return true;
            },
          },
        ],
      });
    },
    onConfirmDelete: (callback) => {
      present({
        header: "Delete Job?",
        message: "This will mark the job as DELETED for everyone.",
        buttons: [
          "Cancel",
          {
            text: "Delete",
            handler: () => {
              callback();
              if (onJobDeleted) onJobDeleted();
            },
          },
        ],
      });
    },
    onScheduledWerkersFound: () => {
      present({
        header: "Can't delete this job",
        message: "Someone is still Scheduled on this job. Cancel or unassign them first.",
        buttons: ["OK"],
      });
    },
    onSuccess: (message) => {
      present({
        header: "Success",
        message,
        buttons: ["OK"],
      });
    },
    onError: (message) => {
      present({
        header: "Error",
        message,
        buttons: ["OK"],
      });
    },
  });

  useEffect(() => {
    if (!jobId) {
      setJobData(null);
      return;
    }

    setLoading(true);
    
    // Set up real-time listener for job data
    const jobDocRef = doc(db, "jobsDocs", jobId);
    
    const unsubscribe = onSnapshot(jobDocRef, 
      (jobDocSnap) => {
        try {
          if (jobDocSnap.exists()) {
            const data = jobDocSnap.data();
            
            const jobInfo = {
              JobDocId: isScheduler ? jobId : (data.jobInfo?.core?.uniqueJobId || ""),
              MyJobDocId: !isScheduler ? jobId : undefined,
              WerkerUid: data.docStamp?.userInfoTo?.uid || "",
              WerkerFirstName: data.docStamp?.userInfoTo?.firstName || "",
              WerkerLastName: data.docStamp?.userInfoTo?.lastName || "",
              WerkerProfilePicURL: data.docStamp?.userInfoTo?.profilePicURL || "",
              SchedulerUid: data.docStamp?.userInfoFrom?.uid || "",
              SchedFirstName: data.docStamp?.userInfoFrom?.firstName || "",
              SchedLastName: data.docStamp?.userInfoFrom?.lastName || "",
              SchedProfilePic: data.docStamp?.userInfoFrom?.profilePicURL || "",
              SchedUid: data.docStamp?.userInfoFrom?.uid || "",
              TotalJobSlots: data.jobInfo?.core?.numJobSlots || 0,
              UniqueJobDocId: data.jobInfo?.core?.uniqueJobId || "",
              JobIdentifier: data.jobInfo?.core?.shiftIdentifier || "",
              Pay: data.jobInfo?.core?.pay || "",
              Company: data.jobInfo?.core?.company || "",
              StartDate: data.jobInfo?.core?.dateTimeStart || "",
              EndDate: data.jobInfo?.core?.dateTimeFinish || "",
              Location: data.jobInfo?.core?.location || "",
              LocationCoordinates: data.jobInfo?.core?.locationCoordinates || "",
              Notes: data.jobInfo?.core?.shiftNotes || "",
              IsPaidJob: data.jobInfo?.status?.isPaidJob || false,
              WerkersPaid: data.jobInfo?.status?.isPaidWerkers || false,
              SchedJobStatus: data.jobInfo?.status?.jobStatusSched || "",
              WerkerJobStatus: data.jobInfo?.status?.jobStatusWerker || "",
              PaidWerker: data.jobInfo?.status?.isPaidWerker || false,
              IsNewMessage: data.jobInfo?.status?.isNewMessage || false,
              IsCancelled: data.jobInfo?.status?.jobCancelledByScheduler || false,
              ScheduledCount: data.jobInfo?.status?.scheduledCount || 0,
            };
            
            setJobData(jobInfo);
            
            // For scheduler view, also populate SchedJobData
            if (isScheduler) {
              setSchedJobData({
                JobDocId: data.jobInfo?.core?.uniqueJobId || "",
                ShiftIdentifier: data.jobInfo?.core?.shiftIdentifier || "",
                SchedUid: data.docStamp?.userInfoFrom?.uid || "",
                DateDay: data.jobInfo?.core?.dateTimeStart ? format(new Date(data.jobInfo.core.dateTimeStart.substring(0, 19)), "M/d/yy") : "",
                EndDay: data.jobInfo?.core?.dateTimeFinish ? format(new Date(data.jobInfo.core.dateTimeFinish.substring(0, 19)), "M/d/yy") : "",
                StartDateTime: data.jobInfo?.core?.dateTimeStart ? format(new Date(data.jobInfo.core.dateTimeStart.substring(0, 19)), "h:mma") : "",
                FinishDateTime: data.jobInfo?.core?.dateTimeFinish ? format(new Date(data.jobInfo.core.dateTimeFinish.substring(0, 19)), "h:mma") : "",
                StartDateTimeRaw: data.jobInfo?.core?.dateTimeStart || "",
                FinishDateTimeRaw: data.jobInfo?.core?.dateTimeFinish || "",
                Company: data.jobInfo?.core?.company || "",
                Location: data.jobInfo?.core?.location || "",
                LocationCoordinates: data.jobInfo?.core?.locationCoordinates || "",
                Pay: data.jobInfo?.core?.pay || "",
                ShiftNotes: data.jobInfo?.core?.shiftNotes || "",
                NumJobSlots: data.jobInfo?.core?.numJobSlots || 0,
                JobStatusSched: data.jobInfo?.status?.jobStatusSched || "",
                JobStatusWerker: data.jobInfo?.status?.jobStatusWerker || "",
                IsPaidJob: data.jobInfo?.status?.isPaidJob || false,
                PaidWerker: data.jobInfo?.status?.isPaidWerker || false,
                PaidWerkers: data.jobInfo?.status?.isPaidWerkers || false,
                JobCancelledByScheduler: data.jobInfo?.status?.jobCancelledByScheduler || false,
                CreatedAt: data.docStamp?.timestamp?.createdAt || 0,
                UpdatedAt: data.docStamp?.timestamp?.updatedAt || 0,
                IsRead: data.jobInfo?.status?.isRead || false,
                IsNewMessage: data.jobInfo?.status?.isNewMessage || false,
                scheduledCount: data.jobInfo?.status?.scheduledCount || 0,
                seriesId: data.jobInfo?.core?.seriesId || data.seriesId || null,
                seriesIndex: data.jobInfo?.core?.seriesIndex || data.seriesIndex || null,
                seriesTotal: data.jobInfo?.core?.seriesTotal || data.seriesTotal || null,
              });
            }
            
            // For werker view, fetch other scheduled werkers
            if (!isScheduler && jobInfo.UniqueJobDocId && jobInfo.SchedulerUid) {
              const werkersQuery = query(
                collectionGroup(db, "jobsDocs"),
                where("jobInfo.core.uniqueJobId", "==", jobInfo.UniqueJobDocId),
                where("docStamp.userInfoFrom.uid", "==", jobInfo.SchedulerUid),
                where("jobInfo.status.jobStatusWerker", "==", WORKFLOW_WERKER_TYPES.Scheduled)
              );
              
              getDocs(werkersQuery).then((werkersSnap) => {
                const werkers: JobData[] = [];
                werkersSnap.forEach((doc) => {
                  const d = doc.data();
                  if (d.docStamp?.userInfoTo?.uid !== profile.UserId) {
                    werkers.push({
                      JobDocId: d.jobInfo?.core?.uniqueJobId || "",
                      WerkerUid: d.docStamp?.userInfoTo?.uid || "",
                      WerkerFirstName: d.docStamp?.userInfoTo?.firstName || "",
                      WerkerLastName: d.docStamp?.userInfoTo?.lastName || "",
                      WerkerProfilePicURL: d.docStamp?.userInfoTo?.profilePicURL || "",
                      SchedulerUid: d.docStamp?.userInfoFrom?.uid || "",
                      SchedFirstName: d.docStamp?.userInfoFrom?.firstName || "",
                      SchedLastName: d.docStamp?.userInfoFrom?.lastName || "",
                      SchedProfilePic: d.docStamp?.userInfoFrom?.profilePicURL || "",
                      TotalJobSlots: d.jobInfo?.core?.numJobSlots || 0,
                      UniqueJobDocId: d.jobInfo?.core?.uniqueJobId || "",
                      JobIdentifier: d.jobInfo?.core?.shiftIdentifier || "",
                      Pay: d.jobInfo?.core?.pay || "",
                      Company: d.jobInfo?.core?.company || "",
                      StartDate: d.jobInfo?.core?.dateTimeStart || "",
                      EndDate: d.jobInfo?.core?.dateTimeFinish || "",
                      Location: d.jobInfo?.core?.location || "",
                      LocationCoordinates: d.jobInfo?.core?.locationCoordinates || "",
                      Notes: d.jobInfo?.core?.shiftNotes || "",
                      IsPaidJob: d.jobInfo?.status?.isPaidJob || false,
                      WerkersPaid: d.jobInfo?.status?.isPaidWerkers || false,
                      SchedJobStatus: d.jobInfo?.status?.jobStatusSched || "",
                      WerkerJobStatus: d.jobInfo?.status?.jobStatusWerker || "",
                      PaidWerker: d.jobInfo?.status?.isPaidWerker || false,
                      IsNewMessage: d.jobInfo?.status?.isNewMessage || false,
                      IsCancelled: d.jobInfo?.status?.jobCancelledByScheduler || false,
                      ScheduledCount: d.jobInfo?.status?.scheduledCount || 0,
                    });
                  }
                });
                setScheduledWerkers(werkers);
              }).catch((error) => {
                console.error("Error fetching scheduled werkers:", error);
              });
            }
          }
          
          setLoading(false);
        } catch (error) {
          console.error("Error processing job data:", error);
          setLoading(false);
        }
      },
      (error) => {
        console.error("Error in job snapshot listener:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [jobId, isScheduler, profile.UserId]);

  const openChat = () => {
    if (!jobData) return;
    
    // Chat is always stored on the UniqueJobDocId (main job document)
    const chatJobId = jobData.UniqueJobDocId || jobId;
    
    // Find the chat from myChats array that matches this job
    const jobChat = (myChats as any[]).find((chat: any) => chat?.Key === chatJobId);
    
    if (!jobChat) {
      // Still try with chatJobId as fallback
      setModalChatId(chatJobId);
    } else {
      setModalChatId(jobChat.Key);
    }
    
    setIsChatModalOpen(true);
  };

  const handleTakeJob = async () => {
    if (!jobData || !jobData.MyJobDocId) return;
    
    Sentry.addBreadcrumb({ category: "job", message: `Werker taking job (desktop): ${jobData.UniqueJobDocId}`, level: "info" });
    const jobDocRef = doc(db, "jobsDocs", jobData.UniqueJobDocId);
    const myJobDocRef = doc(db, "jobsDocs", jobData.MyJobDocId);

    try {
      let notifyScheduler = false;

      await runTransaction(db, async (transaction) => {
        const [jobSnap, myJobSnap] = await Promise.all([
          transaction.get(jobDocRef),
          transaction.get(myJobDocRef)
        ]);
        
        if (!jobSnap.exists()) {
          throw new Error("Job no longer exists.");
        }
        if (!myJobSnap.exists()) {
          throw new Error("Your job record is missing. Please refresh.");
        }

        const myJobData: any = myJobSnap.data();
        const currentWerkerStatus = myJobData.jobInfo.status.jobStatusWerker;

        if (currentWerkerStatus === WORKFLOW_WERKER_TYPES.Scheduled) {
          throw new Error("You already took this job.");
        }
        if (currentWerkerStatus === WORKFLOW_WERKER_TYPES.Past) {
          throw new Error("This job has already passed.");
        }

        const jobDataSnap: any = jobSnap.data();
        const totalSlots = jobDataSnap.jobInfo.core.numJobSlots;
        const scheduledCount = jobDataSnap.jobInfo.status.scheduledCount || 0;

        if (scheduledCount >= totalSlots) {
          throw new Error("Dang... just missed it.");
        }

        const newCount = scheduledCount + 1;

        transaction.update(jobDocRef, {
          "jobInfo.status.scheduledCount": newCount,
          ...(newCount >= totalSlots && {
            "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Filled,
          }),
        });

        transaction.update(myJobDocRef, {
          "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Scheduled,
          "docStamp.timestamp.updatedAt": Date.now(),
        });

        notifyScheduler = newCount >= totalSlots;
      });

      // Update job feed
      try {
        const myFeedRef = doc(db, "users", profile.UserId, "jobFeed", jobData.UniqueJobDocId);
        await setDoc(
          myFeedRef,
          {
            status: "scheduled",
            updatedAt: Date.now(),
            isRead: true,
            dismissed: false,
            dismissedAt: null,
          },
          { merge: true }
        );
      } catch (e) {
        console.error("Failed to update jobFeed", e);
      }

      if (notifyScheduler) {
        try {
          fx_FB_SendNotification(
            profile.UserId, 
            [jobData.SchedulerUid], 
            PUSH_NOTIFICATION_TYPES.SchedulerFilledJobs, 
            {
              shiftIdentifier: jobData.JobIdentifier,
              uniqueJobDocId: jobData.UniqueJobDocId,
            }
          );
          fx_FB_WerkerUpdateAll(profile.UserId, jobData.SchedulerUid, jobData.UniqueJobDocId, "");
          fx_FB_WerkerFeedUpdateAll(profile.UserId, jobData.SchedulerUid, jobData.UniqueJobDocId, {
            action: "filled",
          });
        } catch (notifyError) {
          console.error("[JobDetailPanel] Post-take notification failed:", notifyError);
          Sentry.captureException(notifyError);
        }
      }

      present({
        header: "Success",
        message: "You've taken the job!",
        buttons: ["OK"],
      });
    } catch (error: any) {
      present({
        header: "Unable to take job",
        message: error.message ?? "Please try again.",
        buttons: ["OK"],
      });
      Sentry.captureException(error);
    }
  };

  const handleMarkCompleted = async () => {
    if (!jobData || !jobData.MyJobDocId) return;

    try {
      const werkersOnJob = query(
        collectionGroup(db, "jobsDocs"),
        where("docStamp.userInfoFrom.uid", "==", jobData.SchedulerUid),
        where("docStamp.userInfoTo.uid", "!=", jobData.SchedulerUid),
        where("jobInfo.core.uniqueJobId", "==", jobData.UniqueJobDocId),
        where("jobInfo.status.jobStatusWerker", "in", [WORKFLOW_WERKER_TYPES.Past])
      );

      const querySnapshot = await getDocs(werkersOnJob);
      const pastWerkersCount = querySnapshot.size;

      if (
        jobData.SchedJobStatus === WORKFLOW_SCHEDULER_TYPES.Filled &&
        jobData.TotalJobSlots - pastWerkersCount === 1
      ) {
        const setJobIdSched = doc(db, "jobsDocs", jobData.UniqueJobDocId);
        await updateDoc(setJobIdSched, {
          "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Past,
        });
      }

      const setJobIdMe = doc(db, "jobsDocs", jobData.MyJobDocId);
      await updateDoc(setJobIdMe, {
        "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Past,
      });

      present({
        header: "Success",
        message: "Job marked as completed!",
        buttons: ["OK"],
      });
    } catch (error: any) {
      present({
        header: "Error",
        message: error.message ?? "Unable to mark as completed.",
        buttons: ["OK"],
      });
    }
  };

  const handleUndo = async () => {
    if (!jobData || !jobData.MyJobDocId) return;

    try {
      if (jobData.SchedJobStatus === WORKFLOW_SCHEDULER_TYPES.Past) {
        const setJobIdSched = doc(db, "jobsDocs", jobData.UniqueJobDocId);
        await updateDoc(setJobIdSched, {
          "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Filled,
        });
      }
      
      const setJobIdMe = doc(db, "jobsDocs", jobData.MyJobDocId);
      await updateDoc(setJobIdMe, {
        "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Scheduled,
      });

      present({
        header: "Success",
        message: "Status changed back to Scheduled.",
        buttons: ["OK"],
      });
    } catch (error: any) {
      present({
        header: "Error",
        message: error.message ?? "Unable to undo.",
        buttons: ["OK"],
      });
    }
  };

  // Scheduler action handlers - using hook
  const handleSchedShiftReminder = handleReminder;
  // After the user confirms "Cancel Job?", chain a second prompt asking whether
  // they still want to pay the werker(s). "No" sets `paymentWaivedByScheduler`
  // so the cancelled job is excluded from Payments → Unpaid.
  const handleSchedCancel = () => {
    const werkerCount = schedJobData?.scheduledCount || 0;
    const plural = werkerCount === 1 ? "Werker" : "Werkers";
    // Defer so the first alert finishes dismissing before we present the next.
    setTimeout(() => {
      present({
        header: `Do you still want to pay the ${plural} for this job?`,
        buttons: [
          { text: "No", handler: () => { cancelJob(true); } },
          { text: "Yes", handler: () => { cancelJob(false); } },
        ],
      });
    }, 350);
  };
  const handleMarkComplete = markComplete;
  const handleUnCompleteJob = unCompleteJob;
  const _handleUnCloseOut = unCloseOut;

  const routeToClone = async () => {
    if (!jobData) return;

    // Use callback if provided (desktop mode), otherwise navigate
    if (onCloneRequest) {
      onCloneRequest(jobData.UniqueJobDocId);
    } else {
      history.push({
        pathname: `/ScheduleJob/`,
        state: {
          msgCat: "JobClone",
          jobDocId: jobData.UniqueJobDocId,
        },
      });
    }
  };

  const routeToEdit = async () => {
    if (!jobData) return;
    
    // Use callback if provided (desktop mode), otherwise navigate
    if (onEditRequest) {
      onEditRequest(jobData.UniqueJobDocId);
    } else {
      history.push({
        pathname: `/ScheduleJob/`,
        state: {
          msgCat: "JobEdit",
          jobDocId: jobData.UniqueJobDocId,
        },
      });
    }
  };

  // Delete - using hook
  const handleDeleteJob = deleteJob;

  // Empty state - no job selected
  if (!jobId) {
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          opacity: 0.85,
        }}
      >
        <img
          src="../assets/GorillaJonesNoBackground.png"
          alt="WerkApe"
          style={{ width: "70%", maxWidth: 480, height: "auto", objectFit: "contain" }}
        />
        <p style={{ color: "var(--ion-color-medium)", fontSize: 14, margin: 0 }}>
          Select a job to view details
        </p>
      </div>
    );
  }

  // Loading state
  if (loading || !jobData) {
    return (
      <div 
        style={{ 
          padding: '40px 20px', 
          textAlign: 'center',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <IonSpinner 
          name="crescent"
        />
      </div>
    );
  }

  // Format dates
  const formatDateTime = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return {
        date: format(date, 'EEE, MMM d, yyyy'),
        time: format(date, 'h:mm a'),
      };
    } catch {
      return { date: '', time: '' };
    }
  };

  const start = formatDateTime(jobData.StartDate);
  const end = formatDateTime(jobData.EndDate);
  
  // Calculate hours
  const calculateHours = () => {
    try {
      const startDate = new Date(jobData.StartDate);
      const endDate = new Date(jobData.EndDate);
      const diff = endDate.getTime() - startDate.getTime();
      const hours = diff / (1000 * 60 * 60);
      return hours.toFixed(1);
    } catch {
      return '0';
    }
  };

  const hours = calculateHours();
  
  // Parse notes
  const parsedNotes = parseNotes2(jobData.Notes);
  const mapUrls = generateMapUrls(jobData.LocationCoordinates);

  // ========== SCHEDULER VIEW ==========
  if (isScheduler && schedJobData) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Segment Selector */}
        <div style={{ flexShrink: 0, borderBottom: '1px solid var(--ion-color-light-shade)', padding: '8px 16px' }}>
          <IonSegment
            value={segmentValue}
            onIonChange={(e: any) => setSegmentValue(e.detail.value)}
          >
            <IonSegmentButton
              value="details"
            >
              <IonLabel>
                Details
              </IonLabel>
            </IonSegmentButton>

            <IonSegmentButton
              value="werkers"
            >
              <IonLabel>
                Werkers
              </IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </div>

        {/* Content Area */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
          {segmentValue === "details" ? (
            <SchedDetailsComponent
              jobDocId={jobData.UniqueJobDocId}
              schedJob={schedJobData}
              onEdit={routeToEdit}
              onClone={routeToClone}
              onDelete={handleDeleteJob}
            />
          ) : (
            <SchedWerkersComponent
              jobDocId={jobData.UniqueJobDocId}
              seriesId={schedJobData.seriesId}
              onEditWerkersRequest={onEditWerkersRequest}
            />
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ flexShrink: 0, borderTop: '1px solid var(--ion-color-light-shade)', padding: '12px 16px' }}>
          {schedJobData.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Open && (
            <IonGrid style={{ padding: 0 }}>
              <IonRow>
                <IonCol size="10">
                  {(() => {
                    const hasInvited = (schedJobData.invitedCount ?? 0) > 0;
                    const handleSendInvite = () => {
                      if (schedJobData.seriesId) {
                        present({
                          header: "Invite Werkers",
                          message: "Invite for just this job or the whole series?",
                          buttons: [
                            { text: "Cancel", role: "cancel" },
                            { text: "This Job", handler: () => { if (onEditWerkersRequest) onEditWerkersRequest({ nextView: "selectWerkers", jobId: jobId || undefined }); } },
                            { text: "Whole Series", handler: () => { if (onEditWerkersRequest) onEditWerkersRequest({ nextView: "selectWerkersSeries", seriesId: schedJobData.seriesId || undefined }); } },
                          ],
                        });
                      } else {
                        if (onEditWerkersRequest) onEditWerkersRequest({ nextView: "selectWerkers", jobId: jobId || undefined });
                      }
                    };
                    return (
                      <IonButton
                        onClick={hasInvited ? handleSchedShiftReminder : handleSendInvite}
                        color="secondwarning"
                        fill="solid"
                        expand="block"
                        style={{ margin: 0, height: 42 }}
                      >
                        <IonIcon icon={megaphoneOutline} slot="start" />
                        {hasInvited ? "Re-alert" : "Send Invite"}
                      </IonButton>
                    );
                  })()}
                </IonCol>
                <IonCol size="2">
                  <IonButton
                    onClick={() =>
                      present({
                        header: "Cancel Job?",
                        buttons: ["No", { text: "Yes", handler: handleSchedCancel }],
                      })
                    }
                    color="danger"
                    fill="solid"
                    expand="block"
                    style={{ margin: 0, height: 42 }}
                  >
                    <IonIcon icon={closeOutline} size="large" />
                  </IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          )}

          {schedJobData.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Filled && (
            <IonGrid style={{ padding: 0 }}>
              <IonRow>
                <IonCol size="10">
                  <IonButton
                    onClick={() =>
                      present({
                        header: "Mark as complete? All scheduled werkers will be marked complete as well.",
                        buttons: ["Cancel", { text: "Ok", handler: handleMarkComplete }],
                      })
                    }
                    color="secondwarning"
                    style={{ margin: 0, height: 42 }}
                    fill="solid"
                    expand="block"
                  >
                    Mark Complete
                  </IonButton>
                </IonCol>
                <IonCol size="2">
                  <IonButton
                    onClick={() =>
                      present({
                        header: "Cancel Job?",
                        buttons: ["No", { text: "Yes", handler: handleSchedCancel }],
                      })
                    }
                    color="danger"
                    style={{ margin: 0, height: 42 }}
                    fill="solid"
                    expand="block"
                  >
                    <IonIcon icon={closeOutline} />
                  </IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          )}

          {schedJobData.PaidWerkers === true && (
            <IonButton
              // onClick={() =>
              //   present({
              //     header: "Mark Un-Paid?",
              //     buttons: ["Cancel", { text: "Ok", handler: handleUnCloseOut }],
              //   })
              // }
              color="medium"
              style={{ margin: 0, height: 42 }}
              fill="outline"
              expand="block"
              disabled={schedJobData.JobCancelledByScheduler === true}
            >
              {/* <IonIcon icon={arrowUndo} slot="start" /> */}
              Werkers Paid
            </IonButton>
          )}

          {schedJobData.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Past && schedJobData.PaidWerkers !== true && (
            <IonGrid style={{ padding: 0 }}>
              <IonRow>
                <IonCol size="10">
                  <IonButton
                    color="secondwarning"
                    style={{ margin: 0, height: 42 }}
                    fill="solid"
                    expand="block"
                    disabled={schedJobData.JobCancelledByScheduler === true}
                  >
                    Done
                  </IonButton>
                </IonCol>
                <IonCol size="2">
                  <IonButton
                    onClick={() =>
                      present({
                        header: "Undo Completed Job?",
                        buttons: ["Cancel", { text: "Ok", handler: handleUnCompleteJob }],
                      })
                    }
                    color="medium"
                    style={{ margin: 0, height: 42 }}
                    fill="solid"
                    expand="block"
                    disabled={schedJobData.JobCancelledByScheduler === true}
                  >
                    <IonIcon icon={arrowUndo} />
                  </IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          )}

          <IonButton 
            expand="block" 
            fill="outline"
            color="medium"
            style={{ marginTop: schedJobData.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Open ? '8px' : '0' }}
            onClick={openChat}
          >
            <IonIcon icon={chatboxOutline} slot="start" />
            Chat
          </IonButton>
        </div>

        {/* Chat Modal */}
        <IonModal
          isOpen={isChatModalOpen}
          onDidDismiss={() => {
            setIsChatModalOpen(false);
            setModalChatId(null);
          }}
          style={{ '--width': 'min(800px, 92vw)', '--height': 'min(720px, 85vh)' } as React.CSSProperties}
        >
          <IonHeader>
            <IonToolbar>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: '16px' }}>
                <IonAvatar style={{ width: '40px', height: '40px' }}>
                  <img
                    src={jobData?.WerkerProfilePicURL || "../assets/profilePic.png"}
                    alt=""
                    onError={(e: any) => {
                      e.target.src = "../assets/profilePic.png";
                    }}
                  />
                </IonAvatar>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '1rem' }}>
                    {`${jobData?.WerkerFirstName || ""} ${jobData?.WerkerLastName || ""}`.trim() || "Werker"}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)' }}>
                    {jobData?.JobIdentifier || "Job Chat"}
                  </div>
                </div>
              </div>
              <IonButtons slot="end">
                <IonButton
                  onClick={() => {
                    setIsChatModalOpen(false);
                    setModalChatId(null);
                  }}
                >
                  Close
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <div style={{ height: 'calc(100% - 56px)', overflow: 'hidden' }}>
            {modalChatId ? <ChatPanel chatId={modalChatId} hideHeader /> : <div style={{ padding: '20px' }}>Loading chat...</div>}
          </div>
        </IonModal>
      </div>
    );
  }

  // ========== WERKER VIEW ==========
  // Job detail view
  return (
    <div style={{ padding: '16px', height: '100%', overflowY: 'auto' }}>
      {/* Header with Scheduler/Werker info */}
      <IonCard>
        <IonCardContent>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <IonAvatar>
              <img 
                src={jobData.SchedProfilePic} 
                alt="" 
              />
            </IonAvatar>
            <div>
              <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>
                {`${jobData.SchedFirstName} ${jobData.SchedLastName}`}
              </div>
              <div style={{ fontSize: '0.9rem', opacity: 0.7 }}>
                Scheduler
              </div>
            </div>
            {jobData.IsNewMessage && (
              <IonBadge color="danger" style={{ marginLeft: 'auto' }}>
                New Message
              </IonBadge>
            )}
          </div>
          
          {/* Company & Job ID */}
          <div style={{ marginBottom: '8px' }}>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', opacity: 0.92 }}>
              {jobData.Company}
            </div>
            <div style={{ fontSize: '0.9rem', opacity: 0.7 }}>
              {jobData.JobIdentifier}
            </div>
          </div>
          
          {/* Status badges */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop:'12px' }}>
            {jobData.PaidWerker && (
              <IonBadge color="success">JOB PAID</IonBadge>
            )}
            {jobData.IsCancelled && (
              <IonBadge color="danger">CANCELLED</IonBadge>
            )}
            {jobData.WerkerJobStatus && (
              <IonBadge color="medium">{jobData.WerkerJobStatus}</IonBadge>
            )}
          </div>
        </IonCardContent>
      </IonCard>

      {/* Job Details */}
      <IonCard>
        <IonCardContent>
          <IonList lines="none">
            {/* Date & Time */}
            <IonItem>
              <IonIcon icon={calendarOutline} slot="start" />
              <IonLabel>
                <div style={{ fontWeight: 600 }}>{start.date}</div>
                <div style={{ fontSize: '0.9rem', opacity: 0.7, marginTop: '4px' }}>
                  {start.time} - {end.time} • {hours} hrs
                </div>
              </IonLabel>
            </IonItem>

            {/* Pay */}
            <IonItem>
              <IonIcon icon={cashOutline} slot="start" />
              <IonLabel>
                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{jobData.Pay}</div>
              </IonLabel>
            </IonItem>

            {/* Location */}
            <IonItem>
              <IonIcon icon={locationOutline} slot="start" />
              <IonLabel>
                {jobData.LocationCoordinates && (
                  <div style={{ fontWeight: 600 }}>{jobData.LocationCoordinates}</div>
                )}
                <div style={{ fontSize: '0.9rem', opacity: 0.8, marginTop: '4px', whiteSpace: 'normal' }}>
                  {jobData.Location}
                </div>
              </IonLabel>
              {jobData.LocationCoordinates && mapUrls.appleMapsUrl && (
                <IonButton 
                  slot="end" 
                  fill="outline" 
                  size="small"
                  onClick={() => window.open(mapUrls.appleMapsUrl, '_blank')}
                >
                  <IonIcon icon={navigate} />
                </IonButton>
              )}
            </IonItem>
          </IonList>
        </IonCardContent>
      </IonCard>

      {/* Notes */}
      {jobData.Notes && (
        <IonCard>
          <IonCardContent>
            <div style={{ fontWeight: 600, marginBottom: '8px', opacity: 0.9 }}>Notes</div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem', opacity: 0.85 }}>
              {parsedNotes.map((note, i) => (
                <div key={i} style={{ marginBottom: '8px' }}>{note}</div>
              ))}
            </div>
          </IonCardContent>
        </IonCard>
      )}

      {/* Other Scheduled Werkers (for werker view) */}
      {scheduledWerkers.length > 0 && (
        <IonCard>
          <IonCardContent>
            <div style={{ fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IonIcon icon={peopleOutline} />
              Also Scheduled ({scheduledWerkers.length})
            </div>
            <IonList lines="none">
              {scheduledWerkers.map((werker, idx) => (
                <IonItem key={idx}>
                  <IonAvatar slot="start">
                    <img src={werker.WerkerProfilePicURL} alt="" />
                  </IonAvatar>
                  <IonLabel>
                    {werker.WerkerFirstName} {werker.WerkerLastName}
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>
          </IonCardContent>
        </IonCard>
      )}

      {/* Action Buttons */}
      <div style={{ marginTop: '16px', marginBottom: '16px' }}>
        {jobData.WerkerJobStatus === WORKFLOW_WERKER_TYPES.Invited && (
          jobData.TotalJobSlots > 0 && jobData.ScheduledCount >= jobData.TotalJobSlots ? (
            <IonButton 
              expand="block" 
              color="medium"
              disabled={true}
            >
              Job Full
            </IonButton>
          ) : (
            <IonButton 
              expand="block" 
              color="warning"
              onClick={() =>
                present({
                  header: "Werk This Job?",
                  buttons: ["Cancel", { text: "OK", handler: handleTakeJob }],
                })
              }
            >
              Take the Job
            </IonButton>
          )
        )}

        {jobData.WerkerJobStatus === WORKFLOW_WERKER_TYPES.Scheduled && (
          <>
            <IonButton 
              expand="block" 
              color="success"
              onClick={() =>
                present({
                  header: 'Mark as "Completed"?',
                  buttons: ["Cancel", { text: "OK", handler: handleMarkCompleted }],
                })
              }
            >
              <IonIcon icon={checkmark} slot="start" />
              Mark as Completed
            </IonButton>
            <IonButton 
              expand="block" 
              fill="outline"
              color="medium"
              style={{ marginTop: '8px' }}
              onClick={openChat}
            >
              <IonIcon icon={chatboxOutline} slot="start" />
              Chat
            </IonButton>
          </>
        )}

        {jobData.WerkerJobStatus === WORKFLOW_WERKER_TYPES.Past && (
          <>
            <IonButton 
              expand="block" 
              color="medium"
              onClick={() =>
                present({
                  header: 'Return to "Scheduled"?',
                  buttons: ["Cancel", { text: "OK", handler: handleUndo }],
                })
              }
            >
              <IonIcon icon={arrowUndoOutline} slot="start" />
              Undo
            </IonButton>
            <IonButton 
              expand="block" 
              fill="outline"
              color="medium"
              style={{ marginTop: '8px' }}
              onClick={openChat}
            >
              <IonIcon icon={chatboxOutline} slot="start" />
              Chat
            </IonButton>
          </>
        )}
      </div>

      {/* Chat Modal */}
      <IonModal
        isOpen={isChatModalOpen}
        onDidDismiss={() => {
          setIsChatModalOpen(false);
          setModalChatId(null);
        }}
        style={{ '--width': 'min(800px, 92vw)', '--height': 'min(720px, 85vh)' } as React.CSSProperties}
      >
        <IonHeader>
          <IonToolbar>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: '16px' }}>
              <IonAvatar style={{ width: '40px', height: '40px' }}>
                <img
                  src={jobData?.SchedProfilePic || "../assets/profilePic.png"}
                  alt=""
                  onError={(e: any) => {
                    e.target.src = "../assets/profilePic.png";
                  }}
                />
              </IonAvatar>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '1rem' }}>
                  {`${jobData?.SchedFirstName || ""} ${jobData?.SchedLastName || ""}`.trim() || "Scheduler"}
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)' }}>
                  {jobData?.JobIdentifier || "Job Chat"}
                </div>
              </div>
            </div>
            <IonButtons slot="end">
              <IonButton
                onClick={() => {
                  setIsChatModalOpen(false);
                  setModalChatId(null);
                }}
              >
                Close
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <div style={{ height: 'calc(100% - 56px)', overflow: 'hidden' }}>
          {modalChatId ? <ChatPanel chatId={modalChatId} hideHeader /> : <div style={{ padding: '20px' }}>Loading chat...</div>}
        </div>
      </IonModal>
    </div>
  );
};
